// src/components/SessionTimeoutWarning.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/SessionTimeoutWarning.css';

const SessionTimeoutWarning = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60); // Default 60 seconds countdown
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  
  // Check for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Function to refresh the token
  const refreshSession = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No refresh token, must log in again
        setShowWarning(false); // Hide warning before navigation
        navigate('/login');
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
      
      const data = await response.json();
      
      if (data.accessToken) {
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('loginTimestamp', Date.now().toString());
        
        // Hide the warning
        setShowWarning(false);
        // Clear the countdown interval if it exists
        if (countdownInterval) {
          clearInterval(countdownInterval);
          setCountdownInterval(null);
        }
      } else {
        // Token refresh failed, must log in again
        setShowWarning(false); // Hide warning before navigation
        navigate('/login');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      setShowWarning(false); // Hide warning before navigation
      navigate('/login');
    }
  }, [countdownInterval, navigate]);
  
  // Function to log the user out
  const logout = useCallback(() => {
    // Clear the countdown interval if it exists
    if (countdownInterval) {
      clearInterval(countdownInterval);
      setCountdownInterval(null);
    }
    
    // Hide the warning immediately
    setShowWarning(false);
    
    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loginTimestamp');
    
    // Navigate to login page
    navigate('/login');
  }, [countdownInterval, navigate]);
  
  // Check session expiry
  useEffect(() => {
    const checkSession = () => {
      // Check if we're already on the login page
      if (window.location.pathname === '/login') {
        setShowWarning(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      const loginTimestamp = localStorage.getItem('loginTimestamp');
      
      if (!token || !loginTimestamp) {
        setShowWarning(false); // No active session, hide warning
        return;
      }
      
      const expiryTime = parseInt(loginTimestamp) + (60 * 60 * 1000); // 1 hour expiry
      const currentTime = Date.now();
      const remainingTime = expiryTime - currentTime;
      
      // Show warning when less than 5 minutes remaining (300000 ms)
      if (remainingTime <= 300000 && remainingTime > 0) {
        setShowWarning(true);
        
        // Set countdown to match remaining seconds, capped at 60
        const remainingSeconds = Math.min(Math.floor(remainingTime / 1000), 60);
        setCountdown(remainingSeconds);
        
        // Start countdown if not already started
        if (!countdownInterval) {
          const interval = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                // When countdown reaches 0, log out
                clearInterval(interval);
                logout();
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          
          setCountdownInterval(interval);
        }
      } else if (remainingTime <= 0) {
        // Session expired
        logout();
      }
    };
    
    // Check immediately
    checkSession();
    
    // Then check every minute
    const interval = setInterval(checkSession, 60000);
    
    // Listen for route changes to hide warning when navigating to login
    const handleRouteChange = () => {
      if (window.location.pathname === '/login') {
        setShowWarning(false);
      }
    };
    
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      clearInterval(interval);
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [countdownInterval, logout, navigate]);
  
  // Don't render anything if not showing warning
  if (!showWarning) {
    return null;
  }
  
  return (
    <div className={`session-timeout-overlay ${isMobile ? 'mobile' : ''}`}>
      <div className="session-timeout-modal">
        <div className="session-timeout-header">
          <h3>⏱️ Session About to Expire</h3>
        </div>
        <div className="session-timeout-body">
          <p>Your session is about to expire due to inactivity.</p>
          <p>You will be automatically logged out in <strong>{countdown}</strong> seconds.</p>
        </div>
        <div className="session-timeout-footer">
          <button className="timeout-button continue" onClick={refreshSession}>
            Continue Session
          </button>
          <button className="timeout-button logout" onClick={logout}>
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;